import React from 'react';
import dynamic from 'next/dynamic';
import * as TablerIcons from '@tabler/icons-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';

// ─────────────────────────────────────────────────────────────────────────────
// Registre des sources d'icônes côté site public (Next.js Pages Router).
// Pendant côté admin de src/components/custom-fields/iconSources.js.
//
// Stratégie de chargement (cf. « lazy par source ») :
//   - tabler : import statique (déjà bundlé, les thèmes l'utilisent partout).
//              Rendu synchrone → aucun flash sur l'existant.
//   - lucide : lazy par icône via next/dynamic + dynamicIconImports.
//   - fa     : lazy par pack via next/dynamic (le pack du style n'est chargé
//              qu'au 1er usage d'une icône de ce style).
//
// ⚠️ On utilise `next/dynamic` (PAS React.lazy) : en Pages Router, React.lazy
//    n'est pas supporté au rendu serveur. next/dynamic gère le SSR proprement.
//
// Format `name` : tabler → "IconHeart" ; lucide → "heart" ; fa → "<style>:<iconName>".
// ─────────────────────────────────────────────────────────────────────────────

// ── Lucide : un composant dynamic par nom (chunk chargé à la demande) ──────────
const lucideCache = {};
function getLucideComponent(name) {
    if (!dynamicIconImports[name]) return null; // nom absent de cette version de lucide-react
    if (!lucideCache[name]) {
        // dynamicIconImports[name]() → Promise<{ default: IconComponent }>
        lucideCache[name] = dynamic(dynamicIconImports[name], { ssr: false });
    }
    return lucideCache[name];
}

// ── Font Awesome : lazy par pack (chargé au 1er usage d'une icône du style) ────
const FA_PACK_IMPORT = {
    solid: () => import('@fortawesome/free-solid-svg-icons').then((m) => m.fas),
    regular: () => import('@fortawesome/free-regular-svg-icons').then((m) => m.far),
    brands: () => import('@fortawesome/free-brands-svg-icons').then((m) => m.fab),
};
const faCache = {};
function parseFaName(name) {
    const idx = name.indexOf(':');
    if (idx === -1) return { style: 'solid', iconName: name };
    return { style: name.slice(0, idx), iconName: name.slice(idx + 1) };
}
function getFaComponent(name) {
    if (faCache[name]) return faCache[name];
    const { style, iconName } = parseFaName(name);
    const importPack = FA_PACK_IMPORT[style] || FA_PACK_IMPORT.solid;
    faCache[name] = dynamic(
        async () => {
            const [pack, faReact] = await Promise.all([
                importPack(),
                import('@fortawesome/react-fontawesome'),
            ]);
            const FontAwesomeIcon = faReact.FontAwesomeIcon;
            // pack indexé par export PascalCase (faHeart) → retrouver par iconName.
            const def = Object.values(pack).find((d) => d && d.iconName === iconName);
            const Wrapper = ({ size = 20, color, ...rest }) =>
                def
                    ? <FontAwesomeIcon icon={def} color={color} style={{ width: size, height: size }} {...rest} />
                    : null;
            return Wrapper;
        },
        { ssr: false }
    );
    return faCache[name];
}

const SOURCES = {
    tabler: {
        key: 'tabler',
        label: 'Tabler',
        getComponent: (name) => TablerIcons[name] || null,
        lazy: false,
    },
    lucide: {
        key: 'lucide',
        label: 'Lucide',
        getComponent: getLucideComponent,
        lazy: true,
    },
    fa: {
        key: 'fa',
        label: 'Font Awesome',
        getComponent: getFaComponent,
        lazy: true,
    },
};

export function getSource(key) {
    return SOURCES[key] || SOURCES.tabler;
}

export default SOURCES;
